import { Box, IconButton } from '@fullstory/components';
import React, { useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import css from './ZoomOverlay.module.scss';
import { useTrendsChartState } from '../TrendsChart';

interface ZoomOverlayProps {
  isZoomed: boolean;
  onZoomIn: (startDate: Date, endDate: Date) => void;
  onZoomOut: () => void;
}

export function ZoomOverlay({ isZoomed, onZoomIn, onZoomOut }: ZoomOverlayProps) {
  const intl = useIntl();
  const { height, width, xScale, dragData, setDragData, overlay } = useTrendsChartState();
  const overlayData = useMemo(() => {
    const startX = dragData?.startX ?? 0;
    const endX = dragData?.endX ?? 0;
    const startPoint = Math.min(startX, endX);
    const endPoint = Math.max(startX, endX);

    return {
      startPoint,
      width: endPoint - startPoint,
    };
  }, [dragData]);

  const handleZoom = useCallback(() => {
    const startDate = xScale.invert(overlayData.startPoint);
    const endDate = xScale.invert(overlayData.startPoint + overlayData.width);
    onZoomIn(startDate, endDate);
    setDragData(null);
  }, [onZoomIn, overlayData.startPoint, overlayData.width, setDragData, xScale]);

  return (
    <foreignObject data-component="DragOverlay" width={width} height={height}>
      {dragData && dragData.startX !== null && dragData.endX !== null && (
        <>
          <Box
            height={height}
            width={overlayData.width}
            style={{
              left: `${overlayData.startPoint}px`,
            }}
            className={css.zoomOverlay}
          />
          {!dragData.isDragging &&
            overlay &&
            createPortal(
              <Box
                height={height}
                width={overlayData.width}
                style={{
                  zIndex: 1,
                  top: 0,
                  position: 'absolute',
                  left: `${overlayData.startPoint}px`,
                  cursor: 'zoom-in',
                }}
                onClick={handleZoom}
              />,
              overlay,
            )}
        </>
      )}

      {isZoomed &&
        overlay &&
        createPortal(
          <Box
            style={{
              position: 'absolute',
              top: `4px`,
              left: `${width + 12}px`,
              zIndex: 2,
            }}
            data-component="ZoomOutButton"
          >
            <IconButton
              title={intl.formatMessage({ defaultMessage: 'Zoom out', id: '/UnJ3S' })}
              iconName="undo"
              onClick={onZoomOut}
              variant="outline"
              className={css.zoomOutButton}
              inverse
            />
          </Box>,
          overlay,
        )}
    </foreignObject>
  );
}
